// Global Instructions Rule Applied!
//
// Internal Agents — Phase 2 UI HTTP service (design: docs/INTERNAL_AGENTS_DESIGN.md).
// Talks to the Python backend only (CLAUDE.md — never Supabase directly).
// Every response is strict-validated with its Zod schema before it reaches UI
// state; a response that fails validation is an error, never coerced.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import type { RouteDefinition } from '../../../core/utils/endpoints.ts'
import {
  AgentSignal,
  AgentMemory,
  AgentInstance,
  AgentRun,
  CompanyBriefSection,
  DealBriefSection,
  LpBriefSection,
  ProspectBriefSection,
  LpFundraisingRow,
  LpFundraisingHistoryRow,
  InvestmentCriterion,
  AgentWrittenContent,
  CompanyState,
  CompanyKpi,
  DealState,
  ProspectState,
  LpState,
  validateAgentSignalsList,
  validateAgentMemoriesList,
  validateAgentInstancesList,
  validateAgentRunsList,
  validateCompanyBriefSections,
  validateDealBriefSections,
  validateLpBriefSections,
  validateProspectBriefSections,
  validateLpFundraisingRows,
  validateLpFundraisingHistoryRows,
  validateInvestmentCriteriaList,
  validateAgentWrittenContentList,
  validateCompanyStatesList,
  validateCompanyKpisList,
  validateDealStatesList,
  validateProspectStatesList,
  validateLpStatesList,
  DocusignEnvelope,
  validateDocusignEnvelopesList,
  AdminAgentRun,
  AgentQueueSummary,
  BackfillManifest,
  DeepenPreview,
  validateAdminAgentRunsList,
  validateBackfillManifest,
  validateDeepenPreview
} from '../../../state_manager/data_models/internal_agents_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const getJson = async (url: string, accessToken: string | null): Promise<unknown> => {
  const response = await fetch(url, { method: 'GET', headers: jsonHeaders(accessToken) })
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(`Request failed: ${response.status} ${response.statusText}`)
  }
  const json = (await response.json()) as { data?: unknown }
  return json.data ?? []
}

export const fetchAgentSignals = async (
  params: { context_table?: string; context_id?: string; status?: string; limit?: number },
  accessToken: string | null = null
): Promise<AgentSignal[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.SIGNALS)
  const qs = new URLSearchParams()
  if (params.context_table) qs.set('context_table', params.context_table)
  if (params.context_id) qs.set('context_id', params.context_id)
  if (params.status) qs.set('status', params.status)
  if (params.limit) qs.set('limit', String(params.limit))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return validateAgentSignalsList(await getJson(`${base}${suffix}`, accessToken))
}

export const setAgentSignalStatus = async (
  signalId: string,
  status: 'open' | 'acknowledged' | 'dismissed' | 'resolved',
  reason: string | null,
  accessToken: string | null = null
): Promise<void> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.SIGNALS)
  const response = await fetch(`${base}/${signalId}/status`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { status, reason } })
  })
  if (!response.ok) {
    throw new Error(`Failed to update signal: ${response.status} ${response.statusText}`)
  }
}

export const fetchAgentMemories = async (
  instanceId: string,
  includeSuperseded: boolean,
  accessToken: string | null = null
): Promise<AgentMemory[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.MEMORIES)
  const qs = `?instance_id=${encodeURIComponent(instanceId)}&include_superseded=${includeSuperseded}`
  return validateAgentMemoriesList(await getJson(`${base}${qs}`, accessToken))
}

export const fetchAgentInstances = async (
  agentTypeKey: string | null = null,
  accessToken: string | null = null
): Promise<AgentInstance[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.INSTANCES)
  const qs = agentTypeKey ? `?agent_type_key=${encodeURIComponent(agentTypeKey)}` : ''
  return validateAgentInstancesList(await getJson(`${base}${qs}`, accessToken))
}

export const fetchAgentRuns = async (
  instanceId: string,
  limit: number,
  accessToken: string | null = null
): Promise<AgentRun[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.RUNS)
  const qs = `?instance_id=${encodeURIComponent(instanceId)}&limit=${limit}`
  return validateAgentRunsList(await getJson(`${base}${qs}`, accessToken))
}

export const fetchCompanyBrief = async (
  accountId: string,
  accessToken: string | null = null
): Promise<CompanyBriefSection[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.COMPANY_BRIEF)
  const qs = `?account_id=${encodeURIComponent(accountId)}`
  return validateCompanyBriefSections(await getJson(`${base}${qs}`, accessToken))
}

/** Raise a task from a signal. The signal's own context decides what the task
 *  is linked to (the backend derives it), so only the reviewed task record
 *  travels. Human-triggered only — agents never create tasks. */
export const createTaskFromSignal = async (
  signalId: string,
  taskRecord: unknown,
  accessToken: string | null = null
): Promise<unknown> => {
  // Composed from the SIGNALS base, matching setAgentSignalStatus above
  // (API_ROUTES.INTERNAL_AGENTS.SIGNAL_CREATE_TASK documents the route).
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.SIGNALS)
  const response = await fetch(`${base}/${signalId}/create-task`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { task: taskRecord } })
  })
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string; message?: string }>(response)
    throw new Error(
      body?.error || body?.message || `Task creation failed: ${response.status} ${response.statusText}`
    )
  }
  const json = (await response.json()) as { data?: unknown }
  return json.data ?? null
}

export const fetchDealBrief = async (
  dealId: string,
  accessToken: string | null = null
): Promise<DealBriefSection[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DEAL_BRIEF)
  const qs = `?deal_id=${encodeURIComponent(dealId)}`
  return validateDealBriefSections(await getJson(`${base}${qs}`, accessToken))
}

export const fetchLpBrief = async (
  accountId: string,
  accessToken: string | null = null
): Promise<LpBriefSection[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.LP_BRIEF)
  const qs = `?account_id=${encodeURIComponent(accountId)}`
  return validateLpBriefSections(await getJson(`${base}${qs}`, accessToken))
}

export const fetchLpFundraisingPosition = async (
  accountId: string,
  accessToken: string | null = null
): Promise<LpFundraisingRow[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.LP_FUNDRAISING)
  const qs = `?account_id=${encodeURIComponent(accountId)}`
  return validateLpFundraisingRows(await getJson(`${base}${qs}`, accessToken))
}

export const fetchLpFundraisingHistory = async (
  accountId: string,
  accessToken: string | null = null
): Promise<LpFundraisingHistoryRow[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.LP_FUNDRAISING_HISTORY)
  const qs = `?account_id=${encodeURIComponent(accountId)}`
  return validateLpFundraisingHistoryRows(await getJson(`${base}${qs}`, accessToken))
}

export const fetchProspectBrief = async (
  prospectId: string,
  accessToken: string | null = null
): Promise<ProspectBriefSection[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PROSPECT_BRIEF)
  const qs = `?prospect_id=${encodeURIComponent(prospectId)}`
  return validateProspectBriefSections(await getJson(`${base}${qs}`, accessToken))
}

export const fetchInvestmentCriteria = async (
  accessToken: string | null = null
): Promise<InvestmentCriterion[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.INVESTMENT_CRITERIA)
  return validateInvestmentCriteriaList(await getJson(base, accessToken))
}

const postJson = async (
  url: string,
  payload: unknown,
  accessToken: string | null,
  failureLabel: string
): Promise<void> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: payload })
  })
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string; message?: string }>(response)
    throw new Error(
      body?.error || body?.message || `${failureLabel}: ${response.status} ${response.statusText}`
    )
  }
}

/** postJson, but returning the response body. The admin operations all report
 *  what they did (manifest counts, rows cancelled, the resume warning), so the
 *  caller needs the payload, not just a 2xx. */
const postJsonWithData = async (
  url: string,
  payload: unknown,
  accessToken: string | null,
  failureLabel: string
): Promise<unknown> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: payload })
  })
  const body = await readJsonBody<{ data?: unknown; error?: string; message?: string }>(response)
  if (!response.ok) {
    // message first, then error — handle_route_custom_error uses `message`
    // while the auth guard uses `error`; reading them the other way round
    // turns a readable 403 into a status-code string.
    throw new Error(
      body?.message || body?.error || `${failureLabel}: ${response.status} ${response.statusText}`
    )
  }
  return body?.data
}

// ── admin operations (Configuration → Follower Agents) ──────────────────────

/** Queue historical processing. Exactly one scope: account_id, fund_id,
 *  deal_id, prospect_id, lp_status_ids or scope:'all'. dry_run returns the
 *  manifest without writing. */
export const postAgentBackfill = async (
  payload: {
    agent_type_key: string
    scope?: 'all'
    account_id?: string
    fund_id?: string
    deal_id?: string
    prospect_id?: string
    lp_status_ids?: string[]
    batch_size?: number
    stagger_minutes?: number
    dry_run: boolean
  },
  accessToken: string | null = null
): Promise<BackfillManifest> =>
  validateBackfillManifest(
    await postJsonWithData(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.BACKFILL),
      payload,
      accessToken,
      'Backfill failed'
    )
  )

// Deepen backfill (design §7.4.2): the history BEHIND an already-processed
// follower's first backfill floor. floor null = everything earlier; dry_run
// returns the preview (ceiling, interactions in range, windows) and writes
// nothing. Admin-gated like the backfill — it commits model spend.
export const postAgentDeepen = async (
  payload: { instance_id: string; floor: string | null; dry_run: boolean },
  accessToken: string | null = null
): Promise<DeepenPreview> =>
  validateDeepenPreview(
    await postJsonWithData(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DEEPEN),
      payload,
      accessToken,
      'Deepen failed'
    )
  )

export const runAgentNow = async (
  // run_mode: an operator run of one of the type's own scheduled modes
  // (cadence_review) instead of the default interaction-window pass — the
  // backend routes it to controller.run_mode_now.
  payload: { agent_type_key: string; subject_id: string; run_mode?: string },
  accessToken: string | null = null
): Promise<unknown> =>
  postJsonWithData(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.RUN_NOW),
    payload,
    accessToken,
    'Run failed'
  )

export const provisionAgentInstances = async (
  agentTypeKey: string,
  accessToken: string | null = null
): Promise<unknown> =>
  postJsonWithData(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PROVISION),
    { agent_type_key: agentTypeKey },
    accessToken,
    'Provisioning failed'
  )

export const fetchAdminAgentRuns = async (
  params: {
    agent_type_key?: string
    status?: string
    trigger?: string
    date_from?: string
    date_to?: string
    limit?: number
    offset?: number
  },
  accessToken: string | null = null
): Promise<{ data: AdminAgentRun[]; has_more: boolean; count: number }> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.ADMIN_RUNS)
  const qs = new URLSearchParams()
  if (params.agent_type_key) qs.set('agent_type_key', params.agent_type_key)
  if (params.status) qs.set('status', params.status)
  if (params.trigger) qs.set('trigger', params.trigger)
  if (params.date_from) qs.set('date_from', params.date_from)
  if (params.date_to) qs.set('date_to', params.date_to)
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.offset) qs.set('offset', String(params.offset))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const json = await getListEnvelope(`${base}${suffix}`, accessToken)
  return {
    data: validateAdminAgentRunsList(json.data ?? []),
    has_more: json.has_more === true,
    count: listCount(json.count)
  }
}

/** The windowed follower list behind the Followers table's infinite scroll.
 *  fetchAgentInstances stays the whole-fleet read for the per-subject Agent
 *  tab, which has to find its own instance in the list. */
/** The Followers table's sort keys — the RPC's whitelist, named by the row
 *  key the column shows, so a column's dataIndex is its sort key. The three
 *  counter columns are deliberately not sortable. */
export type AdminInstancesSortKey =
  | 'subject_name'
  | 'agent_type_key'
  | 'agent_instances__status'
  | 'agent_instances__last_processed_interaction_at'
  | 'agent_instances__follow_until'

/** Every filter and the sort are optional and resolved server-side: the
 *  table is a 100-row window, so nothing is narrowed or ordered here. */
export type AdminInstancesQuery = {
  agent_type_key?: string
  status?: string
  /** Case-insensitive contains match on the subject name. */
  subject?: string
  /** ISO instant: followers processed before it, plus the never-processed. */
  processed_before?: string
  sort_key?: AdminInstancesSortKey
  sort_dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export const fetchAdminAgentInstances = async (
  params: AdminInstancesQuery,
  accessToken: string | null = null
): Promise<{ data: AgentInstance[]; has_more: boolean; count: number }> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.ADMIN_INSTANCES)
  const qs = new URLSearchParams()
  if (params.agent_type_key) qs.set('agent_type_key', params.agent_type_key)
  if (params.status) qs.set('status', params.status)
  if (params.subject) qs.set('subject', params.subject)
  if (params.processed_before) qs.set('processed_before', params.processed_before)
  if (params.sort_key) qs.set('sort_key', params.sort_key)
  if (params.sort_dir) qs.set('sort_dir', params.sort_dir)
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.offset) qs.set('offset', String(params.offset))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  const json = await getListEnvelope(`${base}${suffix}`, accessToken)
  return {
    data: validateAgentInstancesList(json.data ?? []),
    has_more: json.has_more === true,
    count: listCount(json.count)
  }
}

/** The list envelope ({ data, has_more, count }) drives the admin tables'
 *  infinite scroll and their "100 of 2,142 rows" footer, so this reads the
 *  whole body rather than getJson's data-only view. */
const getListEnvelope = async (
  url: string,
  accessToken: string | null
): Promise<{ data?: unknown; has_more?: unknown; count?: unknown }> => {
  const response = await fetch(url, { method: 'GET', headers: jsonHeaders(accessToken) })
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(`Request failed: ${response.status} ${response.statusText}`)
  }
  return (await response.json()) as { data?: unknown; has_more?: unknown; count?: unknown }
}

/** A list envelope's count is a non-negative integer; anything else is a
 *  malformed envelope, not something to coerce. */
const listCount = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new Error('list envelope count is not a non-negative integer')
  }
  return value
}

export const fetchAgentQueueSummary = async (
  accessToken: string | null = null
): Promise<AgentQueueSummary> =>
  (await getJson(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.ADMIN_QUEUE_SUMMARY),
    accessToken
  )) as AgentQueueSummary

/** Pause/resume the pg_cron tick — the real fleet pause. Resuming with a due
 *  backlog comes back with a `warning` the caller should surface. */
export const setAgentQueueActive = async (
  active: boolean,
  accessToken: string | null = null
): Promise<{ active: boolean; queued_total: number; queued_due_now: number; warning?: string }> =>
  (await postJsonWithData(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.ADMIN_QUEUE_ACTIVE),
    { active },
    accessToken,
    'Queue update failed'
  )) as { active: boolean; queued_total: number; queued_due_now: number; warning?: string }

/** The operator triggers: the interaction backfill enqueues 'backfill', the
 *  highlight trackers' Generate-history enqueues 'manual'. Queue-wide
 *  operations (cancel, re-stagger) scope to both by default. */
export const OPERATOR_TRIGGERS = ['backfill', 'manual']

/** Trash queued runs by id, agent type and/or trigger list — at least one. */
export const cancelQueuedAgentRuns = async (
  payload: { run_ids?: string[]; agent_type_key?: string; triggers?: string[] },
  accessToken: string | null = null
): Promise<{ cancelled: number; triggers: string[] | null; run_ids: string[] }> =>
  (await postJsonWithData(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.ADMIN_QUEUE_CANCEL),
    payload,
    accessToken,
    'Cancel failed'
  )) as { cancelled: number; triggers: string[] | null; run_ids: string[] }

/** Re-space queued runs. `triggers` omitted = the operator triggers
 *  (backfill + manual — the interaction backfill and the highlight trackers'
 *  Generate-history runs); ['*'] = every queued row. */
export const restaggerQueuedAgentRuns = async (
  payload: { stagger_minutes?: number; agent_type_key?: string; triggers?: string[] },
  accessToken: string | null = null
): Promise<{ restaggered: number; stagger_minutes: number; triggers: string[] | null; last_run_at: string | null }> =>
  (await postJsonWithData(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.ADMIN_QUEUE_RESTAGGER),
    payload,
    accessToken,
    'Re-stagger failed'
  )) as { restaggered: number; stagger_minutes: number; triggers: string[] | null; last_run_at: string | null }

/** Re-queue a terminal (error / unconfigured) run as a fresh queued row.
 *  `exists` = the instance already had a queued row, which covers the retry. */
export const retryAgentRun = async (
  runId: string,
  accessToken: string | null = null
): Promise<{ status: 'queued' | 'exists'; run_id: string; retry_of: string }> =>
  (await postJsonWithData(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.ADMIN_RUNS_RETRY),
    { run_id: runId },
    accessToken,
    'Retry failed'
  )) as { status: 'queued' | 'exists'; run_id: string; retry_of: string }

export const saveInvestmentCriterion = async (
  payload: {
    id?: string | null
    criterion_key?: string
    label?: string
    guidance?: string | null
    priority?: number
    deal_type_id?: string | null
    active?: boolean
    clear_deal_type?: boolean
  },
  accessToken: string | null = null
): Promise<void> =>
  postJson(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.INVESTMENT_CRITERIA_SAVE),
    payload,
    accessToken,
    'Save failed'
  )

export const deleteInvestmentCriterion = async (
  id: string,
  accessToken: string | null = null
): Promise<void> =>
  postJson(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.INVESTMENT_CRITERIA_DELETE),
    { id },
    accessToken,
    'Delete failed'
  )

export const correctCompanyBrief = async (
  payload: { account_id: string; section_key: string; correction: string; memory_type?: string },
  accessToken: string | null = null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.COMPANY_BRIEF_CORRECT),
    {
      method: 'POST',
      headers: jsonHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    }
  )
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string }>(response)
    throw new Error(body?.error || `Correction failed: ${response.status} ${response.statusText}`)
  }
}

export const fetchAgentWrittenContent = async (
  params: { output_type?: string; status?: string; subject_id?: string; limit?: number },
  accessToken: string | null = null
): Promise<AgentWrittenContent[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.WRITTEN_CONTENT)
  const qs = new URLSearchParams()
  if (params.output_type) qs.set('output_type', params.output_type)
  if (params.status) qs.set('status', params.status)
  if (params.subject_id) qs.set('subject_id', params.subject_id)
  if (params.limit) qs.set('limit', String(params.limit))
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return validateAgentWrittenContentList(await getJson(`${base}${suffix}`, accessToken))
}

export const setAgentInstanceStatus = async (
  instanceId: string,
  status: 'active' | 'paused',
  accessToken: string | null = null
): Promise<void> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.INSTANCES)
  const response = await fetch(`${base}/${instanceId}/status`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { status } })
  })
  if (!response.ok) {
    throw new Error(`Failed to update instance: ${response.status} ${response.statusText}`)
  }
}

export const fetchCompanyStates = async (
  accountId: string,
  accessToken: string | null = null
): Promise<CompanyState[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.COMPANY_STATES)
  const qs = `?account_id=${encodeURIComponent(accountId)}`
  return validateCompanyStatesList(await getJson(`${base}${qs}`, accessToken))
}

export const overrideCompanyState = async (
  payload: {
    account_id: string
    state_key: string
    action: 'set_condition' | 'edit_summary' | 'close'
    state_id?: string | null
    condition_key?: string | null
    summary?: string | null
  },
  accessToken: string | null = null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.COMPANY_STATES_OVERRIDE),
    {
      method: 'POST',
      headers: jsonHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    }
  )
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string }>(response)
    throw new Error(body?.error || `State override failed: ${response.status} ${response.statusText}`)
  }
}

export const fetchDealStates = async (
  dealId: string,
  agentTypeKey: string | null = null,
  accessToken: string | null = null
): Promise<DealState[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DEAL_STATES)
  const qs = new URLSearchParams({ deal_id: dealId })
  if (agentTypeKey) qs.set('agent_type_key', agentTypeKey)
  return validateDealStatesList(await getJson(`${base}?${qs.toString()}`, accessToken))
}

export const overrideDealState = async (
  payload: {
    deal_id: string
    state_key: string
    action: 'set_condition' | 'edit_summary' | 'close'
    state_id?: string | null
    condition_key?: string | null
    summary?: string | null
  },
  accessToken: string | null = null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DEAL_STATES_OVERRIDE),
    {
      method: 'POST',
      headers: jsonHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    }
  )
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string }>(response)
    throw new Error(body?.error || `State override failed: ${response.status} ${response.statusText}`)
  }
}

export const fetchProspectStates = async (
  prospectId: string,
  agentTypeKey: string | null = null,
  accessToken: string | null = null
): Promise<ProspectState[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PROSPECT_STATES)
  const qs = new URLSearchParams({ prospect_id: prospectId })
  if (agentTypeKey) qs.set('agent_type_key', agentTypeKey)
  return validateProspectStatesList(await getJson(`${base}?${qs.toString()}`, accessToken))
}

export const overrideProspectState = async (
  payload: {
    prospect_id: string
    state_key: string
    action: 'set_condition' | 'edit_summary' | 'close'
    state_id?: string | null
    condition_key?: string | null
    summary?: string | null
  },
  accessToken: string | null = null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PROSPECT_STATES_OVERRIDE),
    {
      method: 'POST',
      headers: jsonHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    }
  )
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string }>(response)
    throw new Error(body?.error || `State override failed: ${response.status} ${response.statusText}`)
  }
}

export const fetchLpStates = async (
  accountId: string,
  agentTypeKey: string | null = null,
  accessToken: string | null = null
): Promise<LpState[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.LP_STATES)
  const qs = new URLSearchParams({ account_id: accountId })
  if (agentTypeKey) qs.set('agent_type_key', agentTypeKey)
  return validateLpStatesList(await getJson(`${base}?${qs.toString()}`, accessToken))
}

export const overrideLpState = async (
  payload: {
    account_id: string
    state_key: string
    action: 'set_condition' | 'edit_summary' | 'close'
    state_id?: string | null
    condition_key?: string | null
    summary?: string | null
  },
  accessToken: string | null = null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.LP_STATES_OVERRIDE),
    {
      method: 'POST',
      headers: jsonHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    }
  )
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string }>(response)
    throw new Error(body?.error || `State override failed: ${response.status} ${response.statusText}`)
  }
}

/** Raise a task from a state card. The state's own subject decides what the
 *  task is linked to (the backend derives it), so only the reviewed task
 *  record travels — the createTaskFromSignal pattern. */
const createTaskFromState = async (
  baseRoute: RouteDefinition,
  stateId: string,
  taskRecord: unknown,
  accessToken: string | null
): Promise<unknown> => {
  const base = buildFullUrl(BACKEND_BASE_URL, baseRoute)
  const response = await fetch(`${base}/${stateId}/create-task`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { task: taskRecord } })
  })
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string; message?: string }>(response)
    throw new Error(
      body?.error || body?.message || `Task creation failed: ${response.status} ${response.statusText}`
    )
  }
  const json = (await response.json()) as { data?: unknown }
  return json.data ?? null
}

export const createTaskFromCompanyState = async (
  stateId: string,
  taskRecord: unknown,
  accessToken: string | null = null
): Promise<unknown> =>
  // Composed from the COMPANY_STATES base, matching overrideCompanyState above
  // (API_ROUTES.INTERNAL_AGENTS.COMPANY_STATE_CREATE_TASK documents the route).
  createTaskFromState(
    API_ROUTES.INTERNAL_AGENTS.COMPANY_STATES,
    stateId,
    taskRecord,
    accessToken
  )

export const createTaskFromDealState = async (
  stateId: string,
  taskRecord: unknown,
  accessToken: string | null = null
): Promise<unknown> =>
  createTaskFromState(API_ROUTES.INTERNAL_AGENTS.DEAL_STATES, stateId, taskRecord, accessToken)

export const createTaskFromProspectState = async (
  stateId: string,
  taskRecord: unknown,
  accessToken: string | null = null
): Promise<unknown> =>
  createTaskFromState(API_ROUTES.INTERNAL_AGENTS.PROSPECT_STATES, stateId, taskRecord, accessToken)

export const createTaskFromLpState = async (
  stateId: string,
  taskRecord: unknown,
  accessToken: string | null = null
): Promise<unknown> =>
  createTaskFromState(API_ROUTES.INTERNAL_AGENTS.LP_STATES, stateId, taskRecord, accessToken)

export const fetchCompanyKpis = async (
  accountId: string,
  accessToken: string | null = null
): Promise<CompanyKpi[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.COMPANY_KPIS)
  const qs = `?account_id=${encodeURIComponent(accountId)}`
  return validateCompanyKpisList(await getJson(`${base}${qs}`, accessToken))
}

export const fetchDocusignEnvelopes = async (
  status: string | null = null,
  accessToken: string | null = null
): Promise<DocusignEnvelope[]> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.DOCUSIGN_ENVELOPES)
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return validateDocusignEnvelopesList(await getJson(`${base}${qs}`, accessToken))
}

const postDocusignAction = async (
  route: RouteDefinition,
  payload: Record<string, unknown>,
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route), {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: payload })
  })
  if (!response.ok) {
    const body = await readJsonBody<{ error?: string }>(response)
    throw new Error(body?.error || `Request failed: ${response.status} ${response.statusText}`)
  }
}

/** Manually attach an unmatched envelope to a deal: links the timeline, wakes
 *  the execution follower, fires the docusign_received signal. */
export const linkDocusignEnvelope = async (
  envelopeId: string,
  dealId: string,
  accessToken: string | null = null
): Promise<void> =>
  postDocusignAction(
    API_ROUTES.INTERNAL_AGENTS.DOCUSIGN_ENVELOPES_LINK,
    { envelope_id: envelopeId, deal_id: dealId },
    accessToken
  )

export const dismissDocusignEnvelope = async (
  envelopeId: string,
  accessToken: string | null = null
): Promise<void> =>
  postDocusignAction(
    API_ROUTES.INTERNAL_AGENTS.DOCUSIGN_ENVELOPES_DISMISS,
    { envelope_id: envelopeId },
    accessToken
  )

export const retireAgentMemory = async (
  memoryId: string,
  accessToken: string | null = null
): Promise<void> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.MEMORIES)
  const response = await fetch(`${base}/${memoryId}/retire`, {
    method: 'POST',
    headers: jsonHeaders(accessToken)
  })
  if (!response.ok) {
    throw new Error(`Failed to retire memory: ${response.status} ${response.statusText}`)
  }
}
